import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Users, FileText, DollarSign, Mail, PlusCircle, TrendingUp, Coffee, MessageSquare, Gift, ClipboardList } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import Navbar from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, accent, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="bg-card border border-border rounded-xl p-5"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${accent ? "bg-accent/10" : "bg-muted"}`}>
          <Icon size={18} className={accent ? "text-brand-accent" : "text-muted-foreground"} />
        </div>
      </div>
      <p className="font-cormorant text-3xl font-semibold text-foreground mb-1">{value}</p>
      <p className="font-manrope text-sm font-medium text-foreground">{label}</p>
      {sub && <p className="font-manrope text-xs text-muted-foreground mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function AdminDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/dashboard`, { withCredentials: true, headers }).then((r) => setData(r.data)),
      axios.get(`${API}/admin/subscribers/growth`, { withCredentials: true, headers }).then((r) => setGrowth(r.data)).catch(() => {}),
      axios.get(`${API}/admin/referrals/leaderboard`, { withCredentials: true, headers }).then((r) => setReferrers(r.data || [])).catch(() => {}),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const d = data || {};
  const subs = d.subscribers || {};
  const posts = d.posts || {};
  const inquiries = d.inquiries || {};
  const tips = d.tips || {};

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="overline mb-2">Admin</p>
            <h1 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground">Dashboard</h1>
          </div>
          <Link
            to="/admin/editor"
            data-testid="new-post-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors duration-200"
          >
            <PlusCircle size={16} />
            New Post
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total Subscribers" value={subs.total ?? 0} sub={`${subs.free ?? 0} free · ${(subs.monthly ?? 0) + (subs.yearly ?? 0)} paid`} delay={0} />
          <StatCard icon={DollarSign} label="MRR" value={`$${d.mrr ?? 0}`} sub={`${subs.monthly ?? 0} mo · ${subs.yearly ?? 0} yr`} accent delay={0.05} />
          <StatCard icon={FileText} label="Published Posts" value={posts.published ?? 0} sub={`${posts.draft ?? 0} drafts`} delay={0.1} />
          <StatCard icon={Coffee} label="Tips (recent)" value={`$${tips.recent_total ?? 0}`} sub="from last 10 tips" delay={0.15} />
        </div>

        {/* Subscriber Growth */}
        {growth && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="bg-card border border-border rounded-xl p-6 mb-8"
            data-testid="growth-chart-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-cormorant text-xl font-semibold text-foreground">Subscriber Growth</h2>
                <p className="font-manrope text-xs text-muted-foreground">
                  +{growth.new_last_7} last 7 days · +{growth.new_last_30} last 30 days
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="source-breakdown">
                {(growth.sources || []).slice(0, 5).map((s) => (
                  <span key={s.source} className="font-manrope text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
                    {s.source}: <span className="font-semibold text-foreground">{s.count}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growth.series || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(24, 56%, 46%)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="hsl(24, 56%, 46%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fontFamily: "Manrope" }}
                    tickFormatter={(d) => d.slice(5)}
                    interval="preserveStartEnd"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 10, fontFamily: "Manrope" }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(value, key) => [value, key === "total" ? "Total subscribers" : "New that day"]}
                    labelFormatter={(d) => d}
                    contentStyle={{ fontFamily: "Manrope", fontSize: 12, borderRadius: 8 }}
                  />
                  <Area type="monotone" dataKey="total" stroke="hsl(24, 56%, 46%)" strokeWidth={2} fill="url(#growthFill)" />
                  <Area type="monotone" dataKey="new" stroke="hsl(24, 30%, 70%)" strokeWidth={1} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {/* Second row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={Mail} label="New Inquiries" value={inquiries.new ?? 0} sub={`${inquiries.total ?? 0} total`} delay={0.2} />
          <StatCard icon={TrendingUp} label="Paid Subscribers" value={(subs.monthly ?? 0) + (subs.yearly ?? 0)} sub={`${subs.monthly ?? 0} monthly + ${subs.yearly ?? 0} yearly`} delay={0.25} />
          <Link to="/admin/leads" data-testid="dashboard-leads-link" className="block">
            <StatCard icon={ClipboardList} label="Leads" value="View" sub="Homepage capture form → CSV" delay={0.3} />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Posts */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-cormorant text-xl font-semibold text-foreground">Recent Posts</h2>
              <Link to="/admin/posts" className="font-manrope text-sm text-brand-accent hover:text-brand-accent-hover transition-colors">
                Manage all
              </Link>
            </div>
            {(d.recent_posts || []).length === 0 ? (
              <div className="text-center py-8">
                <p className="font-manrope text-sm text-muted-foreground mb-3">No posts yet</p>
                <Link to="/admin/editor" className="font-manrope text-sm text-brand-accent hover:underline">Create your first post</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {(d.recent_posts || []).map((post) => (
                  <div key={post.id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-manrope text-sm font-medium text-foreground truncate">{post.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`font-manrope text-xs px-2 py-0.5 rounded-full ${
                          post.status === "published" ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {post.status}
                        </span>
                        {post.visibility === "paid" && (
                          <span className="font-manrope text-xs px-2 py-0.5 rounded-full bg-accent/10 text-brand-accent">Premium</span>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/admin/editor/${post.id}`}
                      className="ml-4 font-manrope text-xs text-brand-accent hover:text-brand-accent-hover transition-colors shrink-0"
                    >
                      Edit
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="space-y-4">
            {/* Inquiries */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-cormorant text-xl font-semibold text-foreground">New Inquiries</h2>
                <Link to="/admin/inquiries" className="font-manrope text-xs text-brand-accent hover:text-brand-accent-hover transition-colors">
                  View all
                </Link>
              </div>
              {(d.recent_inquiries || []).length === 0 ? (
                <p className="font-manrope text-sm text-muted-foreground">No new inquiries</p>
              ) : (
                <div className="space-y-3">
                  {(d.recent_inquiries || []).map((inq) => (
                    <div key={inq.id} className="border-b border-border pb-3 last:border-0 last:pb-0">
                      <p className="font-manrope text-sm font-medium text-foreground">{inq.name}</p>
                      <p className="font-manrope text-xs text-muted-foreground">{inq.service_type}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Subscribers */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-cormorant text-xl font-semibold text-foreground">New Subscribers</h2>
                <Link to="/admin/subscribers" className="font-manrope text-xs text-brand-accent hover:text-brand-accent-hover transition-colors">
                  View all
                </Link>
              </div>
              {(d.recent_subscribers || []).length === 0 ? (
                <p className="font-manrope text-sm text-muted-foreground">No subscribers yet</p>
              ) : (
                <div className="space-y-3">
                  {(d.recent_subscribers || []).map((sub) => (
                    <div key={sub.id} className="flex items-center gap-2 border-b border-border pb-3 last:border-0 last:pb-0">
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-cormorant text-xs font-semibold text-foreground shrink-0">
                        {(sub.name || sub.email)[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-manrope text-sm text-foreground truncate">{sub.name || sub.email}</p>
                        <span className={`font-manrope text-xs px-1.5 py-0.5 rounded-full ${
                          sub.tier === "free" ? "bg-muted text-muted-foreground" : "bg-accent/10 text-brand-accent"
                        }`}>
                          {sub.tier}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Top Referrers */}
            <div className="bg-card border border-border rounded-xl p-5" data-testid="top-referrers-card">
              <div className="flex items-center gap-2 mb-4">
                <Gift size={16} className="text-brand-accent" />
                <h2 className="font-cormorant text-xl font-semibold text-foreground">Top Referrers</h2>
              </div>
              {referrers.length === 0 ? (
                <p className="font-manrope text-sm text-muted-foreground">No referrals yet. Share the Refer & Earn page!</p>
              ) : (
                <div className="space-y-3">
                  {referrers.slice(0, 5).map((r, i) => (
                    <div key={r.id} className="flex items-center gap-2.5 border-b border-border pb-3 last:border-0 last:pb-0">
                      <span className="font-cormorant text-lg font-semibold text-brand-accent w-5 shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-manrope text-sm text-foreground truncate">{r.name || r.email}</p>
                        <p className="font-manrope text-xs text-muted-foreground">
                          {r.referral_count} referral{r.referral_count === 1 ? "" : "s"}
                          {(r.rewards_granted || []).length > 0 && " · reward earned"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Admin Nav Links */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: "/admin/posts", label: "Manage Posts", icon: FileText },
            { to: "/admin/subscribers", label: "Subscribers", icon: Users },
            { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
            { to: "/admin/editor", label: "Write New Post", icon: PlusCircle },
          ].map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex flex-col items-center gap-2 p-4 bg-card border border-border rounded-xl hover:border-brand-accent hover:-translate-y-0.5 transition-all duration-200 text-center"
            >
              <Icon size={20} className="text-brand-accent" />
              <span className="font-manrope text-xs font-medium text-foreground">{label}</span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
